import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';

export default function CrmPayrollSettings() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/crm/payroll/settings')
      .then((r) => setForm(r.data?.data || {}))
      .catch((e) => toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/crm/payroll/settings', form);
      setForm(res.data?.data || form);
      toast({ title: 'Settings saved' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-muted-foreground">Loading settings…</div>;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Payroll Settings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label>Default Cycle</Label>
            <Select value={form.default_cycle || 'joining_date'} onValueChange={(v) => set('default_cycle', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="joining_date">Joining Date</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Calculation Day</Label>
            <Input type="number" value={form.salary_calculation_day ?? 1} onChange={(e) => set('salary_calculation_day', Number(e.target.value))} />
          </div>
          <div>
            <Label>Processing Day</Label>
            <Input type="number" value={form.payroll_processing_day ?? 2} onChange={(e) => set('payroll_processing_day', Number(e.target.value))} />
          </div>
          <div>
            <Label>Payment Day</Label>
            <Input type="number" value={form.salary_payment_day ?? 5} onChange={(e) => set('salary_payment_day', Number(e.target.value))} />
          </div>
          <div>
            <Label>Release Hold Days</Label>
            <Input type="number" value={form.release_hold_days ?? 10} onChange={(e) => set('release_hold_days', Number(e.target.value))} />
          </div>
          <div>
            <Label>Custom Period Days</Label>
            <Input type="number" value={form.custom_period_days ?? 30} onChange={(e) => set('custom_period_days', Number(e.target.value))} />
          </div>
          <div>
            <Label>Late Deduction Amount</Label>
            <Input type="number" step="0.01" value={form.late_deduction_amount ?? 0} onChange={(e) => set('late_deduction_amount', e.target.value)} />
          </div>
          <div>
            <Label>Late Deduction Fraction</Label>
            <Input type="number" step="0.0001" value={form.late_deduction_per_day_fraction ?? 0} onChange={(e) => set('late_deduction_per_day_fraction', e.target.value)} />
          </div>
          <div>
            <Label>Half Day Factor</Label>
            <Input type="number" step="0.01" value={form.half_day_factor ?? 0.5} onChange={(e) => set('half_day_factor', e.target.value)} />
          </div>
          <div>
            <Label>OT Rate Multiplier</Label>
            <Input type="number" step="0.01" value={form.overtime_rate_multiplier ?? 1.5} onChange={(e) => set('overtime_rate_multiplier', e.target.value)} />
          </div>
          <div>
            <Label>Company Name</Label>
            <Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} />
          </div>
          <div>
            <Label>Logo Path</Label>
            <Input value={form.company_logo_path || ''} onChange={(e) => set('company_logo_path', e.target.value)} placeholder="/path/to/logo.png" />
          </div>
        </div>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</Button>
      </CardContent>
    </Card>
  );
}
